import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useOrg } from './OrgContext';
import { useAuth } from './AuthContext';
import { syncService } from '../services/syncService';

interface SyncContextType {
  isOnline: boolean;
  isSyncing: boolean;
  pendingSyncItems: number;
  lastSyncTime: Date | null;
  syncData: () => Promise<void>;
  getStorageInfo: () => Promise<any>;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export function useSync() {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within SyncProvider');
  }
  return context;
}

interface SyncProviderProps {
  children: ReactNode;
}

export function SyncProvider({ children }: SyncProviderProps) {
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingSyncItems, setPendingSyncItems] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Set up online/offline listeners
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize sync service with org ID
  useEffect(() => {
    if (currentOrg) {
      syncService.setOrgId(currentOrg.id);
    }
  }, [currentOrg]);

  // Update pending sync items count
  useEffect(() => {
    const updatePendingCount = async () => {
      const status = await syncService.getSyncQueueStatus();
      setPendingSyncItems(status.pending);
    };

    updatePendingCount();

    // Check every 10 seconds
    const interval = setInterval(updatePendingCount, 10000);

    return () => clearInterval(interval);
  }, []);

  const syncData = async () => {
    if (!currentOrg || isSyncing) return;

    setIsSyncing(true);
    try {
      // Pass userId for targeted sync of reserved jobs
      await syncService.syncAllData(currentOrg.id, currentUser?.uid);
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Sync failed:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  const getStorageInfo = async () => {
    return await syncService.getStorageInfo();
  };

  const value = {
    isOnline,
    isSyncing,
    pendingSyncItems,
    lastSyncTime,
    syncData,
    getStorageInfo,
  };

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}
